import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Row, Col, Button, Space, Card, Grid } from 'antd'
import { COLOZOO_SLOGAN } from '../constants/brand'
import SiteFooter from './SiteFooter'
import './Home.css'
import products from '../assets/products.jpg'
import colozooLogo from '../assets/colozoo_logo.svg'
import ReactCountryFlag from 'react-country-flag'
import { MailOutlined, FacebookOutlined, InstagramOutlined } from '@ant-design/icons'
import PageTitle from './PageTitle'
import PageLayout from './PageLayout'
import PageControls from './PageControls'
import UserProfile from './UserProfile'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { updateMetaTags } from '../utils/metaTagManager'

const { Title, Paragraph, Link, Text } = Typography

interface Lngs {
  [key: string]: { nativeName: string }
}

const lngs: Lngs = {
  de: { nativeName: 'Deutsch' },
  // fr: { nativeName: 'Français' },
  // it: { nativeName: 'Italiano' },
  // es: { nativeName: 'Español' },
  // 'en': { nativeName: 'English UK' },
  'en-US': { nativeName: 'English' },
}

// Map language codes to country codes
const countryCodeMap: { [key: string]: string } = {
  de: 'DE',
  fr: 'FR',
  it: 'IT',
  es: 'ES',
  en: 'GB',
  'en-US': 'US',
  pl: 'PL',
  se: 'SE',
  nl: 'NL',
  be: 'BE',
}

// Helper: language code to translation key and flag
const storeLinks = {
  de: {
    url: 'https://www.amazon.de/stores/Colozoo/page/088F6D23-55D5-4281-ACB4-0E0BFBDD99A3',
  },
  fr: {
    url: 'https://www.amazon.fr/stores/Colozoo/page/315C207E-1141-4BBF-83D1-F857DA73F838',
  },
  it: {
    url: 'https://www.amazon.it/stores/Colozoo/page/6FBDACCD-D6A8-4017-971A-15D2488DE0D2',
  },
  es: {
    url: 'https://www.amazon.es/stores/Colozoo/page/938FD6BE-3585-4F1E-A08F-47E8AAEC8615',
  },
  en: {
    url: 'https://www.amazon.co.uk/stores/Colozoo/page/D00BCC46-3519-4856-B012-FD748CFEE7D6',
  },
  'en-US': {
    url: 'https://www.amazon.com/stores/Colozoo/page/C207523A-55EB-4191-B263-65E44BA822FD',
  },
  pl: {
    url: 'https://www.amazon.pl/stores/Colozoo/page/1D334B31-7C3B-4859-B475-2EAE9ED518C4',
  },
  se: {
    url: 'https://www.amazon.se/stores/Colozoo/page/7DB11D18-5CEF-4567-A485-44692B2747AA',
  },
  nl: {
    url: 'https://www.amazon.nl/stores/Colozoo/page/B69123A5-4AFA-4121-8636-A07CD67B15FA',
  },
  be: {
    url: 'https://www.amazon.com.be/stores/page/0AF9CE3E-C9F7-452B-9632-B92B930C0769',
  },
}


const Home = () => {
  const { t, i18n } = useTranslation('home')
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md
  const rightPosition = screens.lg ? '20%' : screens.md ? '18.33%' : 20
  const { user, userProfile } = useAuth()
  const navigate = useNavigate()
  const [userProfileVisible, setUserProfileVisible] = useState(false)

  // Update meta tags for social media sharing
  useEffect(() => {
    const title = t('meta.title')
    const description = t('meta.description')
    
    updateMetaTags({
      title,
      description,
      url: window.location.href,
    })
  }, [i18n.language, t])

  let sloganFontSize = 80
  if (isMobile) sloganFontSize = 50
  // else if (isTablet) sloganFontSize = 60

  return (
    <>
      <PageTitle title={'Colozoo – Creating happiness!'} />
      <PageLayout>
        <div className="home-page">
          {/* Top right controls */}
          <div style={{ position: 'absolute', top: 20, right: rightPosition, zIndex: 10 }}>
            <div style={{ marginBottom: 10 }}>
              <PageControls onUserProfileClick={user ? () => setUserProfileVisible(true) : undefined} />
            </div>
          </div>
          <div className="home-page-main">
            <Row justify="center" style={{ width: '100%', alignItems: 'center' }}>
              <Col xs={24} md={22} lg={20} style={{ zIndex: 2, padding: 20 }}>
                <Row justify="center" style={{ padding: '42px 32px 0 32px' }}>
                  <Col>
                    <img src={colozooLogo} alt="Colozoo Logo" style={{ height: isMobile ? 55 : 74, width: 'auto' }} />
                  </Col>
                </Row>
                <Row justify="center" style={{ padding: '0 32px 16px 32px' }}>
                  <Col>
                    <span
                      style={{
                        fontWeight: 900,
                        fontSize: sloganFontSize,
                        color: '#fff',
                        display: 'block',
                        whiteSpace: 'nowrap',
                        width: '100%',
                        textAlign: 'center',
                        lineHeight: 1.1,
                        // Responsive font scaling
                        fontFamily: 'inherit',
                      }}
                    >
                      <span style={{
                        display: 'inline-block',
                        width: '100%',
                        fontSize: 'clamp(24px, 8vw, ' + sloganFontSize + 'px)',
                        fontWeight: 900,
                        color: '#fff',
                        whiteSpace: 'nowrap',
                        fontFamily: 'inherit',
                      }}>
                        {COLOZOO_SLOGAN}
                      </span>
                    </span>
                  </Col>
                </Row>
                <Row justify="center">
                  <Col xs={24} md={20} lg={18}>
                    <Card
                      style={{
                        background: 'rgba(132, 204, 208, 0.4)',
                        backdropFilter: 'blur(10px)',
                        marginTop: '10px',
                        marginBottom: '20px',
                        padding: '20px 40px',
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        border: 'none',
                      }}
                      bodyStyle={{ padding: 0 }}
                    >
                      <Row gutter={[ 42, 42 ]} align="top" style={{ marginBottom: '20px' }}>
                        <Col xs={24} md={12}>
                          <div style={{ textAlign: 'center' }}>
                            <Title level={2} style={{ color: '#333', marginBottom: '20px', fontWeight: '800' }}>
                              {t('howWeStarted.title')}
                            </Title>
                            <Paragraph style={{ color: '#555', fontSize: '18px', lineHeight: '1.6', fontWeight: '500' }}>
                              {t('howWeStarted.content')}
                            </Paragraph>
                          </div>
                        </Col>
                        <Col xs={24} md={12}>
                          <div style={{ textAlign: 'center' }}>
                            <Title level={2} style={{ color: '#333', marginBottom: '20px', fontWeight: '800' }}>
                              {t('whatMakesOurProductsUnique.title')}
                            </Title>
                            <Paragraph style={{ color: '#555', fontSize: '18px', lineHeight: '1.6', fontWeight: '500' }}>
                              {t('whatMakesOurProductsUnique.content')}
                            </Paragraph>
                          </div>
                        </Col>
                      </Row>
                      <Row justify="center" style={{ marginBottom: '20px' }}>
                        <Col xs={24}>
                          <img
                            src={products}
                            alt="Colozoo Products"
                            style={{
                              width: '100%',
                              display: 'block',
                              margin: '0 auto',
                              borderRadius: '12px',
                              // boxShadow: '0 4px 16px rgba(63, 158, 147, 1)',
                              border: '0px solid rgba(93, 129, 146, 1)',
                            }}
                          />
                        </Col>
                      </Row>
                      <Row gutter={[ 32, 32 ]} align="top">
                        <Col xs={24} md={12}>
                          <div style={{ textAlign: 'center' }}>
                            <Title level={2} style={{ color: '#333', marginBottom: '16px', fontWeight: '800' }}>
                              {t('ourStores')}
                            </Title>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(5, 1fr)',
                                gap: 0,
                                justifyItems: 'center',
                                alignItems: 'center',
                                margin: '0 auto 12px auto',
                                maxWidth: 320,
                              }}
                            >
                              {Object.keys(storeLinks).map((lng) => {
                                const link = storeLinks[lng as keyof typeof storeLinks]
                                const countryCode = countryCodeMap[lng] || 'GB'
                                return (
                                  <Link
                                    key={lng}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: '32px', verticalAlign: 'middle' }}
                                  >
                                    <ReactCountryFlag
                                      countryCode={countryCode}
                                      svg
                                      style={{ width: 32, height: 32 }}
                                      title={countryCode}
                                    />
                                  </Link>
                                )
                              })}
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} md={12}>
                          <div style={{ textAlign: 'center' }}>
                            <Title level={2} style={{ color: '#333', marginBottom: '24px', fontWeight: '800' }}>
                              {t('contactUs')}
                            </Title>
                            <Paragraph>
                              <Space size={24}>
                                <a href="mailto:support@colozoo.de" target="_blank" rel="noopener noreferrer" title="Email">
                                  <MailOutlined style={{ fontSize: 32, color: '#32793e' }} />
                                </a>
                                <a href="https://www.instagram.com/colozoo.official/" target="_blank" rel="noopener noreferrer" title="Instagram">
                                  <InstagramOutlined style={{ fontSize: 32, color: '#ee3f79' }} />
                                </a>
                                <a href="https://www.facebook.com/Colozoo.Official/" target="_blank" rel="noopener noreferrer" title="Facebook">
                                  <FacebookOutlined style={{ fontSize: 32, color: '#1877f3' }} />
                                </a>
                              </Space>
                            </Paragraph>
                          </div>
                        </Col>
                      </Row>

                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
          <SiteFooter />
        </div>
      </PageLayout>
      <UserProfile visible={userProfileVisible} onClose={() => setUserProfileVisible(false)} />
    </>
  )
}

export default Home
